import React from 'react';
import { Helmet } from 'react-helmet-async'; // Import Helmet
import { Link } from 'react-router-dom';
import { Home, Search, Compass, ArrowRight } from 'lucide-react';

// Popular tools that users might be looking for
const popularTools = [
  { path: '/calculator', label: 'Calculator' },
  { path: '/unit-converter', label: 'Unit Converter' },
  { path: '/qr-code-generator', label: 'QR Code Generator' },
  { path: '/password-generator', label: 'Password Generator' },
  { path: '/color-converter', label: 'Color Converter' },
  { path: '/text-counter', label: 'Text Counter' }
];

function NotFound() {
  return (
    <div className="max-w-4xl mx-auto px-4">
      <Helmet>
        <title>404 - Page Not Found</title>
        <meta name="description" content="The page you were looking for could not be found. Browse our collection of free online tools or return to the homepage." />
        {/* Prevent indexing of 404 pages */}
        <meta name="robots" content="noindex" />
      </Helmet>
      <div className="text-center mb-12">
        <h1 className="text-7xl font-bold text-gray-100 mb-4">404</h1>
        <p className="text-2xl text-gray-300 mb-2">Page Not Found</p>
        <p className="text-gray-400">
          The page you're looking for doesn't exist or has been moved.
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-8 mb-12">
        {/* Quick Navigation */}
        <div className="bg-gray-800 p-6 rounded-lg border border-gray-700">
          <div className="flex items-center gap-3 mb-4">
            <Compass className="h-5 w-5 text-purple-400" />
            <h2 className="text-lg font-medium text-gray-100">Popular Tools</h2>
          </div>
          <div className="space-y-2">
            {popularTools.map(tool => (
              <Link
                key={tool.path}
                to={tool.path}
                className="flex items-center justify-between p-3 rounded-lg bg-gray-700 hover:bg-gray-600 transition-colors group"
              >
                <span className="text-gray-300 group-hover:text-white transition-colors">
                  {tool.label}
                </span>
                <ArrowRight className="h-4 w-4 text-gray-400 group-hover:text-white transition-colors" />
              </Link>
            ))}
          </div>
        </div>

        {/* Search Suggestion */}
        <div className="space-y-6">
          <div className="bg-gray-800 p-6 rounded-lg border border-gray-700">
            <div className="flex items-center gap-3 mb-4">
              <Search className="h-5 w-5 text-blue-400" />
              <h2 className="text-lg font-medium text-gray-100">Looking for Something?</h2>
            </div>
            <p className="text-gray-400 mb-4">
              Try browsing our complete collection of tools to find what you need.
            </p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Home className="h-4 w-4" />
              Browse All Tools
            </Link>
          </div>

          {/* Help Box */}
          <div className="bg-gray-800 p-6 rounded-lg border border-gray-700">
            <h2 className="text-lg font-medium text-gray-100 mb-3">Need Help?</h2>
            <p className="text-gray-400 text-sm">
              If you can't find what you're looking for or believe this is an error,
              please try refreshing the page or clearing your browser cache.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default NotFound;